import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ReceptionistDashboard from './components/ReceptionistDashboard';
import LaboratoristDashboard from './components/LaboratoristDashboard';
import ReportForm from './components/ReportForm';
import ReportView from './components/ReportView';
import AdminPanel from './components/AdminPanel';
import Navbar from './components/Navbar';
import PatientDashboard from './components/PatientDashboard';
import PatientAppointments from './components/PatientAppointments';
import PatientAppointmentDetails from './components/PatientAppointmentDetails';
import PatientReports from './components/PatientReports';
import PatientReportDetails from './components/PatientReportDetails';
import AdminDashboard from './components/AdminDashboard';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2A2E5B', // Fillow deep blue
    },
    secondary: {
      main: '#00C6D7', // Fillow teal/cyan
    },
    background: {
      default: '#F4F7FA', // Fillow light gray
      paper: '#fff',
    },
    text: {
      primary: '#222B45', // dark gray/blue
      secondary: '#6E7A99', // muted blue/gray
    },
  },
  shape: {
    borderRadius: 16, // More rounded corners
  },
  typography: {
    fontFamily: 'Inter, Roboto, Arial, sans-serif',
    fontWeightBold: 700,
  },
});

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return null; // Let the AuthLoadingWrapper handle the loading display
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  
  if (isLoading) {
    return null; // Let the AuthLoadingWrapper handle the loading display
  }
  
  return isAuthenticated && user?.role === 'ADMIN' ? children : <Navigate to="/dashboard" />;
};

const ReceptionistRoute = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  
  if (isLoading) {
    return null; // Let the AuthLoadingWrapper handle the loading display
  }
  
  return isAuthenticated && user?.role === 'RECEPTIONIST' ? children : <Navigate to="/dashboard" />;
};

const RoleBasedDashboard = () => {
  const { user } = useAuth();
  
  if (user?.role === 'ADMIN') {
    return <AdminDashboard />;
  }
  
  if (user?.role === 'RECEPTIONIST') {
    return <ReceptionistDashboard />;
  }
  
  if (user?.role === 'LABORATORIST') {
    return <LaboratoristDashboard />;
  }
  
  if (user?.role === 'PATIENT') {
    return <PatientDashboard />;
  }
  
  return <Dashboard />;
};

// Loading wrapper component
const AuthLoadingWrapper = ({ children }) => {
  const { isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AuthLoadingWrapper>
          <Router>
            <div className="App">
              <Navbar />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <PrivateRoute>
                      <RoleBasedDashboard />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/receptionist" 
                  element={
                    <ReceptionistRoute>
                      <ReceptionistDashboard />
                    </ReceptionistRoute>
                  } 
                />
                <Route 
                  path="/report/new" 
                  element={
                    <PrivateRoute>
                      <ReportForm />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/report/:id" 
                  element={
                    <PrivateRoute>
                      <ReportView />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin-panel" 
                  element={
                    <AdminRoute>
                      <AdminPanel/>
                    </AdminRoute>
                  } 
                />
                <Route path="/patient-dashboard" element={<PatientDashboard />} />
                <Route path="/patient-appointments" element={<PatientAppointments />} />
                <Route path="/patient-appointments/:id" element={<PatientAppointmentDetails />} />
                <Route path="/patient-reports" element={<PatientReports />} />
                <Route path="/patient-reports/:id" element={<PatientReportDetails />} />
                <Route path="/" element={<Navigate to="/login" />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
            </div>
          </Router>
        </AuthLoadingWrapper>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 